import { Dispatch } from 'redux';
import { DelayCodeModel } from '../../Model/DelayCodeModel';
import { Getdelaycodelist } from '../../services/DelayListService/DelayListService';

// This action creator fetches the list of delay codes from the API.
// It uses redux-thunk to handle asynchronous operations.

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchDelayCodes = (token: string) => async (dispatch: Dispatch) => {
  // Dispatch an action to indicate that the fetch request has started.
  dispatch({ type: 'FETCH_DELAY_CODES_REQUEST' });

  let attempts = 0;

  while (attempts < MAX_RETRIES) {
    try {
      // Make the API call to fetch delay codes.
      const delayCodes: DelayCodeModel[] = await Getdelaycodelist(token);
      
      // Dispatch an action with the fetched delay codes on success.
      dispatch({ type: 'FETCH_DELAY_CODES_SUCCESS', payload: delayCodes });
      return; // Exit the function if the API call is successful
    } catch (error: any) {
      attempts += 1;
      if (attempts >= MAX_RETRIES) {
        // Dispatch an action with the error message on failure after max retries.
        dispatch({ type: 'FETCH_DELAY_CODES_FAILURE', payload: error.message });
        return;
      }
      // Wait for the specified delay before retrying
      await delay(RETRY_DELAY_MS);
    }
  }
};

/*
Explanation:
- The `fetchDelayCodes` function is an action creator that returns a thunk function.
- The thunk function takes `dispatch` as an argument, allowing it to dispatch actions.
- `dispatch` is a function provided by Redux that sends actions to the Redux store.
- When `fetchDelayCodes` is called, it first dispatches a 'FETCH_DELAY_CODES_REQUEST' action to indicate the start of the fetch operation.
- It then makes an asynchronous API call to fetch the delay codes.
- If the API call is successful, it dispatches a 'FETCH_DELAY_CODES_SUCCESS' action with the fetched delay codes as the payload.
- If the API call fails, it retries the API call up to `MAX_RETRIES` times, waiting for `RETRY_DELAY_MS` milliseconds between each attempt.
- If the API call fails after the maximum number of retries, it dispatches a 'FETCH_DELAY_CODES_FAILURE' action with the error message as the payload.

This approach allows you to handle asynchronous operations with a retry mechanism and update the Redux state based on the outcome of those operations.
*/